import { z } from 'zod';

export const tariffIngestFormatSchema = z.enum([
  'MB_FIXED_WIDTH',
  'BC_MSP_FIXED_WIDTH',
]);

export const tariffIngestModeSchema = z.enum(['VALIDATE', 'APPLY']);

export const tariffIngestIssueSchema = z
  .object({
    code: z.string().min(1),
    message: z.string().min(1),
    sourceCode: z.string().min(1).optional(),
    sourceLine: z.number().int().positive().optional(),
    sourceSpecialtyCode: z.string().length(2).optional(),
  })
  .strict();

export const mbTariffIngestSourceSchema = z
  .object({
    format: z.literal('MB_FIXED_WIDTH').optional().default('MB_FIXED_WIDTH'),
    province: z.literal('MB'),
    rateFile: z.string().min(1),
    descriptionFile: z.string().min(1),
    mode: z.literal('APPLY').optional().default('APPLY'),
  })
  .strict();

export const bcTariffIngestSourceSchema = z
  .object({
    format: z.literal('BC_MSP_FIXED_WIDTH'),
    province: z.literal('BC'),
    tariffFile: z.string().min(1),
    effectiveDate: z.iso.date(),
    mode: tariffIngestModeSchema.optional().default('APPLY'),
  })
  .strict();

export const tariffIngestSourceSchema = z.union([
  bcTariffIngestSourceSchema,
  mbTariffIngestSourceSchema,
]);

const ingestMessageMetadata = {
  jobId: z.guid(),
  createdAt: z.string().min(1).optional(),
};

const bcTariffBulkIngestMessageSchema = bcTariffIngestSourceSchema.extend(
  ingestMessageMetadata
);

const mbTariffBulkIngestMessageSchema = mbTariffIngestSourceSchema.extend(
  ingestMessageMetadata
);

export const tariffBulkIngestMessageSchema = z.union([
  bcTariffBulkIngestMessageSchema,
  mbTariffBulkIngestMessageSchema,
]);

export const bulkTariffIngestSkippedReasonSchema = z.enum([
  'existing-tariff',
  'duplicate-in-file',
  'blank-line',
]);

const createdMBTariffSchema = z
  .object({
    row: z.number(),
    tariff: z.string(),
    prefix: z.number(),
    code: z.string(),
    specialtyCode: z.string(),
    effectiveDate: z.string(),
    rate: z.string(),
  })
  .strict();

const skippedMBTariffSchema = z
  .object({
    row: z.number(),
    tariff: z.string().optional(),
    reason: bulkTariffIngestSkippedReasonSchema,
  })
  .strict();

const failedMBTariffSchema = z
  .object({
    row: z.number(),
    tariff: z.string().optional(),
    reason: z.string(),
  })
  .strict();

export const mbTariffIngestResultSchema = z
  .object({
    success: z.boolean(),
    totalRows: z.number(),
    createdCount: z.number(),
    skippedCount: z.number(),
    failedCount: z.number(),
    updatedCount: z.number(),
    created: z.array(createdMBTariffSchema),
    skipped: z.array(skippedMBTariffSchema),
    failed: z.array(failedMBTariffSchema),
  })
  .strict();

export const bcTariffIngestResultSchema = z
  .object({
    success: z.boolean(),
    mode: tariffIngestModeSchema,
    parsedEffectiveDate: z.iso.date().optional(),
    suppliedEffectiveDate: z.iso.date(),
    parsedCount: z.number().int().nonnegative(),
    newCount: z.number().int().nonnegative(),
    changedCount: z.number().int().nonnegative(),
    unchangedCount: z.number().int().nonnegative(),
    unsupportedCount: z.number().int().nonnegative(),
    failedCount: z.number().int().nonnegative(),
    warnings: z.array(tariffIngestIssueSchema),
    failures: z.array(tariffIngestIssueSchema),
  })
  .strict();

export const tariffIngestResultSchema = z.union([
  bcTariffIngestResultSchema,
  mbTariffIngestResultSchema,
]);

export const bulkTariffIngestJobStatusSchema = z.enum([
  'PENDING',
  'PROCESSING',
  'COMPLETED',
  'COMPLETED_WITH_ERRORS',
  'FAILED',
]);

export const bulkTariffIngestJobSchema = z
  .object({
    jobId: z.guid(),
    status: bulkTariffIngestJobStatusSchema,
    createdAt: z.string().optional(),
    updatedAt: z.string().optional(),
    processingStartedAt: z.string().optional(),
    completedAt: z.string().optional(),
    failedAt: z.string().optional(),
    format: tariffIngestFormatSchema.optional(),
    sourceFormat: tariffIngestFormatSchema.optional(),
    sourceKeys: z.array(z.string()).optional(),
    sourceS3Key: z.string().optional(),
    rateFile: z.string().optional(),
    descriptionFile: z.string().optional(),
    tariffFile: z.string().optional(),
    effectiveDate: z.iso.date().optional(),
    suppliedEffectiveDate: z.iso.date().optional(),
    mode: tariffIngestModeSchema.optional(),
    province: z.string().optional(),
    parsedEffectiveDate: z.iso.date().optional(),
    sourceSHA256: z
      .string()
      .regex(/^[a-f0-9]{64}$/)
      .optional(),
    normalizedCandidatesKey: z.string().optional(),
    diffReportKey: z.string().optional(),
    result: tariffIngestResultSchema.optional(),
    error: z.string().optional(),
  })
  .strict();

export type TariffIngestFormat = z.infer<typeof tariffIngestFormatSchema>;
export type TariffIngestMode = z.infer<typeof tariffIngestModeSchema>;
export type TariffIngestIssue = z.infer<typeof tariffIngestIssueSchema>;
export type MBTariffIngestSource = z.infer<typeof mbTariffIngestSourceSchema>;
export type BCTariffIngestSource = z.infer<typeof bcTariffIngestSourceSchema>;
export type TariffIngestSource = z.infer<typeof tariffIngestSourceSchema>;
export type TariffBulkIngestMessage = z.output<
  typeof tariffBulkIngestMessageSchema
>;
export type BulkTariffIngestSkippedReason = z.infer<
  typeof bulkTariffIngestSkippedReasonSchema
>;
export type BulkTariffIngestResult = z.infer<typeof mbTariffIngestResultSchema>;
export type BCTariffIngestResult = z.infer<typeof bcTariffIngestResultSchema>;
export type TariffIngestResult = z.infer<typeof tariffIngestResultSchema>;
export type BulkTariffIngestJobStatus = z.infer<
  typeof bulkTariffIngestJobStatusSchema
>;
export type BulkTariffIngestJob = z.infer<typeof bulkTariffIngestJobSchema>;

export const isBCTariffIngestResult = (
  result: TariffIngestResult
): result is BCTariffIngestResult => 'parsedCount' in result;
